import React, { useState, useEffect, useMemo } from 'react';
import { Download, Loader2, TrendingUp, TrendingDown, ChevronDown, ChevronRight, IndianRupee, BarChart3, ArrowUpDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import DateRangeFilter from './DateRangeFilter';
import api from '../../services/api';

const formatCurrency = (v) => '₹' + Math.round(v || 0).toLocaleString('en-IN');
const formatNum = (v) => (v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const MARGIN_COLORS = { high: '#059669', medium: '#d97706', low: '#dc2626', negative: '#7f1d1d' };
const getMarginColor = (pct) => pct >= 30 ? MARGIN_COLORS.high : pct >= 15 ? MARGIN_COLORS.medium : pct >= 0 ? MARGIN_COLORS.low : MARGIN_COLORS.negative;

const MarginsPricingReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [view, setView] = useState('margin'); // 'margin' | 'wasp'
  const [sortField, setSortField] = useState('total_revenue');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = {};
        if (dateRange.from) {
          const d = dateRange.from instanceof Date ? dateRange.from : new Date(dateRange.from);
          params.from_date = d.toISOString().split('T')[0];
        }
        if (dateRange.to) {
          const d = dateRange.to instanceof Date ? dateRange.to : new Date(dateRange.to);
          params.to_date = d.toISOString().split('T')[0];
        }
        const res = await api.get('/reports/margins-pricing', { params });
        setData(res.data);
      } catch (e) { console.error('Failed to load margins data:', e); }
      setLoading(false);
    };
    fetchData();
  }, [dateRange]);

  const sorted = useMemo(() => {
    if (!data?.items) return [];
    return [...data.items].sort((a, b) => {
      const av = a[sortField] || 0, bv = b[sortField] || 0;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
  }, [data, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const chartData = useMemo(() => {
    if (!sorted.length) return [];
    if (view === 'margin') {
      return sorted.slice(0, 12).map(p => ({
        name: p.product_name.length > 18 ? p.product_name.slice(0, 18) + '...' : p.product_name,
        margin: p.gross_margin_pct,
        revenue: p.total_revenue,
      }));
    }
    return sorted.slice(0, 12).map(p => ({
      name: p.product_name.length > 18 ? p.product_name.slice(0, 18) + '...' : p.product_name,
      wasp: p.wasp,
      highest: p.highest_rate,
      lowest: p.lowest_rate,
    }));
  }, [sorted, view]);

  const exportCSV = () => {
    if (!sorted.length) return;
    const headers = view === 'margin'
      ? ['Product', 'SKU', 'Qty Sold', 'Revenue', 'WASP', 'Unit Cost', 'Cost Source', 'COGS', 'Margin/Unit', 'Margin %', 'Total Margin']
      : ['Product', 'SKU', 'Qty Sold', 'Revenue', 'WASP', 'Highest Rate', 'Lowest Rate', 'Spread', 'OC Count'];
    const rows = sorted.map(p => view === 'margin'
      ? [p.product_name, p.sku, p.total_qty, p.total_revenue, p.wasp, p.unit_cost, p.cost_source, p.total_cogs, p.gross_margin_per_unit, p.gross_margin_pct, p.total_gross_margin]
      : [p.product_name, p.sku, p.total_qty, p.total_revenue, p.wasp, p.highest_rate, p.lowest_rate, p.price_spread, p.oc_count]
    );
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${view === 'margin' ? 'gross_margin' : 'wasp'}_report.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;

  const summary = data?.summary || {};

  return (
    <div data-testid="margins-pricing-report" className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex gap-1 bg-gray-100 p-0.5 rounded-lg">
          <button data-testid="view-margin-btn" onClick={() => setView('margin')}
            className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-all ${view === 'margin' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
            Gross Margin
          </button>
          <button data-testid="view-wasp-btn" onClick={() => setView('wasp')}
            className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-all ${view === 'wasp' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
            WASP Analysis
          </button>
        </div>
        <div className="flex items-center gap-2">
          <DateRangeFilter onChange={setDateRange} />
          <button data-testid="export-csv-btn" onClick={exportCSV} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50">
            <Download className="w-3 h-3" />CSV
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <div className="bg-white rounded-xl border border-gray-100 p-3">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Products</p>
          <p className="text-lg font-bold text-gray-900 mt-0.5">{summary.total_products || 0}</p>
          <p className="text-[10px] text-gray-400">{summary.total_ocs || 0} OCs</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-3">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Revenue</p>
          <p className="text-lg font-bold mt-0.5" style={{ color: '#24549c' }}>{formatCurrency(summary.total_revenue)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-3">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">COGS</p>
          <p className="text-lg font-bold text-gray-900 mt-0.5">{formatCurrency(summary.total_cogs)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-3">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Gross Margin</p>
          <p className="text-lg font-bold mt-0.5" style={{ color: getMarginColor(summary.overall_margin_pct) }}>{formatCurrency(summary.total_gross_margin)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-3">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Overall Margin %</p>
          <p className="text-lg font-bold mt-0.5" style={{ color: getMarginColor(summary.overall_margin_pct) }}>
            {summary.overall_margin_pct >= 0 ? <TrendingUp className="w-4 h-4 inline mr-1" /> : <TrendingDown className="w-4 h-4 inline mr-1" />}
            {summary.overall_margin_pct}%
          </p>
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <h3 className="text-xs font-bold text-gray-700 mb-3">{view === 'margin' ? 'Gross Margin % by Product' : 'Weighted Average Selling Price by Product'}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 0, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tick={{ fontSize: 10 }} domain={view === 'margin' ? ['auto', 'auto'] : [0, 'auto']} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} width={130} />
              <Tooltip formatter={(v) => view === 'margin' ? `${v}%` : formatCurrency(v)} contentStyle={{ fontSize: 11 }} />
              {view === 'margin' ? (
                <Bar dataKey="margin" name="Margin %" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry, i) => <Cell key={i} fill={getMarginColor(entry.margin)} />)}
                </Bar>
              ) : (
                <>
                  <Bar dataKey="wasp" name="WASP" fill="#24549c" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="highest" name="Highest" fill="#059669" radius={[0, 4, 4, 0]} opacity={0.5} />
                  <Bar dataKey="lowest" name="Lowest" fill="#dc2626" radius={[0, 4, 4, 0]} opacity={0.5} />
                </>
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 w-8">#</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-gray-500">Product</th>
                <SortHeader label="Qty Sold" field="total_qty" current={sortField} dir={sortDir} onClick={toggleSort} />
                <SortHeader label="Revenue" field="total_revenue" current={sortField} dir={sortDir} onClick={toggleSort} />
                <SortHeader label="WASP (₹/unit)" field="wasp" current={sortField} dir={sortDir} onClick={toggleSort} />
                {view === 'margin' ? (
                  <>
                    <SortHeader label="Unit Cost" field="unit_cost" current={sortField} dir={sortDir} onClick={toggleSort} />
                    <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-gray-500">Source</th>
                    <SortHeader label="Margin/Unit" field="gross_margin_per_unit" current={sortField} dir={sortDir} onClick={toggleSort} />
                    <SortHeader label="Margin %" field="gross_margin_pct" current={sortField} dir={sortDir} onClick={toggleSort} />
                    <SortHeader label="Total Margin" field="total_gross_margin" current={sortField} dir={sortDir} onClick={toggleSort} />
                  </>
                ) : (
                  <>
                    <SortHeader label="Highest" field="highest_rate" current={sortField} dir={sortDir} onClick={toggleSort} />
                    <SortHeader label="Lowest" field="lowest_rate" current={sortField} dir={sortDir} onClick={toggleSort} />
                    <SortHeader label="Spread" field="price_spread" current={sortField} dir={sortDir} onClick={toggleSort} />
                    <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-gray-500">OCs</th>
                  </>
                )}
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((p, idx) => (
                <React.Fragment key={idx}>
                  <tr className={`border-t border-gray-50 hover:bg-gray-50/50 transition-colors ${expandedRow === idx ? 'bg-blue-50/30' : ''}`}>
                    <td className="px-4 py-2.5 text-gray-400">{idx + 1}</td>
                    <td className="px-3 py-2.5">
                      <p className="font-medium text-gray-900 truncate max-w-[200px]">{p.product_name}</p>
                      {p.sku && <p className="text-[9px] text-gray-400 mt-0.5">{p.sku}</p>}
                    </td>
                    <td className="px-3 py-2.5 text-gray-700 text-right">{formatNum(p.total_qty)} <span className="text-[9px] text-gray-400">{p.unit}</span></td>
                    <td className="px-3 py-2.5 text-right font-medium text-gray-900">{formatCurrency(p.total_revenue)}</td>
                    <td className="px-3 py-2.5 text-right font-bold" style={{ color: '#24549c' }}>{formatCurrency(p.wasp)}</td>
                    {view === 'margin' ? (
                      <>
                        <td className="px-3 py-2.5 text-right text-gray-700">{p.unit_cost > 0 ? formatCurrency(p.unit_cost) : <span className="text-gray-300">-</span>}</td>
                        <td className="px-3 py-2.5">
                          <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded-full ${p.cost_source === 'BOM' ? 'bg-blue-100 text-blue-700' : p.cost_source === 'Product Master' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-400'}`}>{p.cost_source}</span>
                        </td>
                        <td className="px-3 py-2.5 text-right font-medium" style={{ color: getMarginColor(p.gross_margin_pct) }}>
                          {p.unit_cost > 0 ? formatCurrency(p.gross_margin_per_unit) : <span className="text-gray-300">-</span>}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          {p.unit_cost > 0 ? (
                            <span className="px-2 py-0.5 text-[10px] font-bold rounded-full" style={{ backgroundColor: `${getMarginColor(p.gross_margin_pct)}15`, color: getMarginColor(p.gross_margin_pct) }}>
                              {p.gross_margin_pct}%
                            </span>
                          ) : <span className="text-gray-300">-</span>}
                        </td>
                        <td className="px-3 py-2.5 text-right font-bold" style={{ color: getMarginColor(p.gross_margin_pct) }}>
                          {p.unit_cost > 0 ? formatCurrency(p.total_gross_margin) : <span className="text-gray-300">-</span>}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-2.5 text-right text-emerald-600 font-medium">{formatCurrency(p.highest_rate)}</td>
                        <td className="px-3 py-2.5 text-right text-red-500 font-medium">{formatCurrency(p.lowest_rate)}</td>
                        <td className="px-3 py-2.5 text-right">
                          {p.price_spread > 0 ? (
                            <span className="text-amber-600 font-medium">{formatCurrency(p.price_spread)}</span>
                          ) : <span className="text-gray-300">-</span>}
                        </td>
                        <td className="px-3 py-2.5 text-center text-gray-600">{p.oc_count}</td>
                      </>
                    )}
                    <td className="px-2 py-2.5">
                      {p.customer_breakdown?.length > 0 && (
                        <button data-testid={`expand-row-${idx}`} onClick={() => setExpandedRow(expandedRow === idx ? null : idx)} className="p-1 rounded hover:bg-gray-100">
                          {expandedRow === idx ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
                        </button>
                      )}
                    </td>
                  </tr>
                  {/* Customer breakdown */}
                  {expandedRow === idx && p.customer_breakdown?.length > 0 && (
                    <tr>
                      <td colSpan={view === 'margin' ? 11 : 10} className="px-4 py-3 bg-gray-50/50">
                        <p className="text-[10px] font-semibold text-gray-500 mb-2 uppercase tracking-wider">Customer-wise Breakdown</p>
                        <div className="grid gap-1.5">
                          {p.customer_breakdown.map((cb, ci) => (
                            <div key={ci} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-100">
                              <span className="text-xs font-medium text-gray-800">{cb.customer_name}</span>
                              <div className="flex gap-6 text-xs text-gray-600">
                                <span>Qty: <strong>{formatNum(cb.qty)}</strong></span>
                                <span>Revenue: <strong>{formatCurrency(cb.revenue)}</strong></span>
                                <span>Avg Rate: <strong className="text-blue-700">{formatCurrency(cb.avg_rate)}</strong></span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {sorted.length === 0 && (
                <tr><td colSpan={view === 'margin' ? 11 : 10} className="px-4 py-12 text-center text-gray-400 text-sm">
                  <BarChart3 className="w-8 h-8 mx-auto mb-2 text-gray-300" />No confirmed/dispatched OC data found for this period
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const SortHeader = ({ label, field, current, dir, onClick }) => (
  <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-gray-500 cursor-pointer hover:text-gray-700 select-none whitespace-nowrap" onClick={() => onClick(field)}>
    {label}
    <ArrowUpDown className={`w-3 h-3 inline ml-0.5 ${current === field ? 'text-blue-500' : 'text-gray-300'}`} />
  </th>
);

export default MarginsPricingReport;
